import { useState } from "react";
import { messaging, primaryMedia, type Message } from "../api";
import { ImageOff, Paperclip, Play } from "lucide-react";

// Render a message's primary attachment by kind. Bytes are served (cookie-auth)
// by GET /api/messaging/messages/:id/media.
export function MediaView({
  m,
  onOpen,
  localSrc,
}: {
  m: Message;
  onOpen?: (media: { src: string; kind: "image" | "video"; fileName?: string }) => void;
  // Aperçu local (object URL) d'un message optimiste en cours d'envoi : les bytes
  // ne sont pas encore servis par le backend (id temporaire), on rend le fichier
  // choisi tel quel en attendant que l'écho réel le remplace.
  localSrc?: string;
}) {
  // Un média 404 (supprimé côté plateforme OU simplement pas servi) casse l'<img>/
  // <video> (carré « ? »). On affiche alors un repère NEUTRE et discret — surtout
  // PAS « message supprimé » : un 404 ne prouve pas une suppression, et l'afficher
  // partout (si le connecteur ne sert temporairement aucun média) serait alarmant.
  const [failed, setFailed] = useState(false);
  const media = primaryMedia(m);
  if (!media) return null;
  const src = localSrc ?? messaging.mediaUrl(m.id);

  if (failed) {
    return (
      <div className="media-unavailable">
        <ImageOff size={15} />
        <span>Média indisponible</span>
      </div>
    );
  }

  switch (media.kind) {
    case "image":
      // <button> englobant : ouverture au clavier (Entrée/Espace) + focus visible.
      return (
        <button
          type="button"
          className="media-btn media-zoom"
          aria-label={`Afficher l'image en plein écran${media.fileName ? ` — ${media.fileName}` : ""}`}
          onClick={() => onOpen?.({ src, kind: "image", fileName: media.fileName })}
        >
          <img
            className="media"
            src={src}
            alt={media.fileName ?? "image"}
            loading="lazy"
            onError={() => setFailed(true)}
          />
        </button>
      );
    case "video":
      // Aperçu (1re frame via metadata) ; le clic ouvre la visionneuse plein écran
      // avec les contrôles (façon WhatsApp), plutôt que de jouer dans la bulle.
      return (
        <button
          type="button"
          className="media-btn media-video-wrap media-zoom"
          aria-label={`Lire la vidéo en plein écran${media.fileName ? ` — ${media.fileName}` : ""}`}
          onClick={() => onOpen?.({ src, kind: "video", fileName: media.fileName })}
        >
          <video
            className="media"
            src={src}
            preload="metadata"
            muted
            onError={() => setFailed(true)}
          />
          <span className="media-play">
            <Play size={22} />
          </span>
        </button>
      );
    case "audio":
    case "voice":
      return (
        <audio
          className="media-audio"
          src={src}
          controls
          preload="metadata"
          onError={() => setFailed(true)}
        />
      );
    default:
      return (
        <a className="media-file" href={src} target="_blank" rel="noreferrer">
          <Paperclip size={18} /> {media.fileName ?? "pièce jointe"}
        </a>
      );
  }
}
